import { z } from "zod";

import {
  policyDetailsAccordionType,
  travellersAccordionType,
  policyExtrasAccordionType,
  medicalAccordionType,
  policyExtrasEditPageType,
} from "./middle-panel.schema";

const ContentItemSchema = z.object({
  field: z.string(),
});

const RightPanelContentItemSchema = z.object({
  title: z.string(),
  label: z.string(),
  uniqueId: z.string(),
  mobileImageUrl: z.string().optional(),
  desktopImageUrl: z.string().optional(),
  tabletImageUrl: z.string().optional(),
});

const RenewalRightPanelContentSchema = z.object({
  trustPilotUrl: z.string(),
  brandName: z.string(),
  menuItems: z.record(RightPanelContentItemSchema),
});

const RenewalLoadingStateSchema = z.object({
  title: z.string(),
  "mgnl:template": z.string(),
  mobileContent: z.record(ContentItemSchema),
  desktopContent: z.record(ContentItemSchema),
});

const medicalModalSchema = z.object({
  medicalModalTitle: z.string(),
  medicalModalCtaLabel: z.string(),
  medicalModalContent: z.string(),
  medicalModalSecondaryCtaLabel: z.string().optional(),
  contactNumber: z.string().optional(),
});

const dontWantRenewModalSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
  dontWantToRenewModalCtaLabel: z.string(),
  content: z.string(),
  dontWantToRenewModalSecondaryCtaLabel: z.string(),
  modalInfoContent: z.string(),
  backgroundModalImageUrl: z.object({
    "@id": z.string(),
    "@link": z.string(),
    "@name": z.string(),
    "@path": z.string(),
    "@type": z.string(),
    "@uuid": z.string(),
  }),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const cancelCallModalSchema = z.object({
  cancelCallModalTitle: z.string(),
  cancelCallModalContent: z.string(),
  callUsButtonLabel: z.string(),
  chatNowButtonLabel: z.string(),
  goBackButtonLabel: z.string(),
});

const RenewalFooterContentSchema = z.object({
  continueCtaLable: z.string(),
  dontWantRenewLable: z.string(),
  medicalModal: medicalModalSchema,
  confirmMedicalModal: medicalModalSchema,
  dontMeetConditionMedicalModal: medicalModalSchema,
  dontWantToRenewModal: dontWantRenewModalSchema,
});

const RenewalContactDetailsAreaSchema = z.object({
  contactDetailsAccordionTitle: z.string(),
  organizerDetailsTitle: z.string(),
  emailEditButtonLabel: z.string(),
  telephoneEditButtonLabel: z.string(),
  organizerNameEditButtonLabel: z.string(),
  organizerAddressEditButtonLabel: z.string(),
});

const UnSavedChangesWarningMessageSchema = z.object({
  content: z.string(),
  continueBtn: z.string(),
  goBackBtn: z.string(),
});

const RenewalHeaderAreaSchema = z.object({
  title: z.string(),
  bottomTextMobileLoaded: z.string(),
  bottomTextDesktopLoaded: z.string(),
  bottomTextTabletLoaded: z.string(),
  bottomTextMobileLoading: z.string(),
  bottomTextDesktopLoading: z.string(),
  bottomTextTabletLoading: z.string(),
});

export const RenewalRemoveMTAQuoteModalTypes = z.enum([
  "consent-modal",
  "consent-yes-modal",
  "consent-no-modal",
  "consent-yes-value-zero-modal",
  "consent-creation-error-modal",
]);

const renewalMTAQuoteCreationMenuItemSchema = z.object({
  uniqueId: RenewalRemoveMTAQuoteModalTypes,
  title: z.string(),
  modalContentOne: z.string(),
  modalContentTwo: z.string(),
  modalMiddleContentTitle: z.string(),
  modalMiddleContentDescription: z.string(),
  modalCTAButtonLabelOne: z.string(),
  modalCTAButtonLabelTwo: z.string(),
});

const renewalMTAQuoteCreationModalSchema = z.object({
  errorModalTitle: z.string(),
  errorModalContent: z.string(),
  errorModalCTAButtonLabel: z.string(),
  menuItems: z.array(renewalMTAQuoteCreationMenuItemSchema),
});

// Left menu item schema
export const RenewalSectionType = z.enum([
  "policy-details",
  "travellers",
  "medical-conditions",
  "policy-extras",
  "contact-details",
]);

export const menuItemSchema = z.object({
  label: z.string(),
  uniqueId: RenewalSectionType,
});

const MenuItemSchemaType = z.object({
  menuItems: z.record(menuItemSchema),
});

const renewalPageAreaData = z.object({
  loadingArea: z.array(RenewalLoadingStateSchema),
  leftArea: z.array(MenuItemSchemaType),
  rightArea: z.array(RenewalRightPanelContentSchema),
  btaVotingArea: z.array(z.any()).optional(),
  policyDetailsAccordionArea: z.array(policyDetailsAccordionType),
  travellersAccordionArea: z.array(travellersAccordionType),
  policyExtrasAccordionArea: z.array(policyExtrasAccordionType),
  footerArea: z.array(RenewalFooterContentSchema),
  contactDetailsAccordionArea: z.array(RenewalContactDetailsAreaSchema),
  medicalAccordionArea: z.array(medicalAccordionType),
  editPolicyExtrasPage: z.array(policyExtrasEditPageType),
  unsavedChangesWarningMessage: z.array(UnSavedChangesWarningMessageSchema),
  headerArea: z.array(RenewalHeaderAreaSchema),
  mtaQuoteCreationModalContent: z.array(renewalMTAQuoteCreationModalSchema),
});

export const renewalPageData = z.object({
  renewalPageArea: z.array(renewalPageAreaData),
});

export type RenewalLoadingSchemaType = z.infer<
  typeof RenewalLoadingStateSchema
>;

export type RenewalRightPanelContentSchemaType = z.infer<
  typeof RenewalRightPanelContentSchema
>;
export type RenewalDashboardLeftMenuSchema = z.infer<typeof MenuItemSchemaType>;

export type RenewalPageAreaType = z.infer<typeof renewalPageAreaData>;

export type RenewalPageType = z.infer<typeof renewalPageData>;

// Footer content type
export type RenewalFooterContentType = z.infer<
  typeof RenewalFooterContentSchema
>;
// medical modal type
export type MedicalModalType = z.infer<typeof medicalModalSchema>;

export type DontWantRenewModalType = z.infer<typeof dontWantRenewModalSchema>;
export type RenewalLandingPageHeaderType = z.infer<
  typeof RenewalHeaderAreaSchema
>;
export type CancelCallModalContent = z.infer<typeof cancelCallModalSchema>;

export type RenewalMTAQuoteCreationModalType = z.infer<
  typeof renewalMTAQuoteCreationModalSchema
>;
